import { useEffect, useMemo, useState } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import fuzzysort from 'fuzzysort';
import { useShellStore } from '../model/shellStore';
import { useMenu, isLeaf } from '../navMenu/useMenu';
import { useOpenScreen } from '../tabs/useOpenScreen';
import { NavIcon } from '../navMenu/NavIcon';
import { cn } from '@/shared/lib/cn';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { useAuthStore } from '@/entities/session/model/authStore';

interface Item {
  id: string;
  group: 'screens' | 'commands';
  label: string;
  hint?: string;
  run: () => void;
  node?: Parameters<typeof NavIcon>[0]['node'];
}

/** Ctrl+F / Ctrl+Space: ekranlar (fuzzy) + komutlar. Veri arama (FilterMenu) Part B grid ile bağlanır. */
export function SpotlightDialog() {
  const { t } = useTranslation();
  const open = useShellStore((s) => s.spotlightOpen);
  const setOpen = useShellStore((s) => s.setSpotlight);
  const setSettings = useShellStore((s) => s.setSettings);
  const designMode = useShellStore((s) => s.designMode);
  const setDesignMode = useShellStore((s) => s.setDesignMode);
  const isSysRol = useAuthStore((s) => s.user?.isSysRol ?? false);
  const { flat } = useMenu();
  const openScreen = useOpenScreen();
  const setPrefs = usePrefsStore((s) => s.set);
  const logout = useAuthStore((s) => s.logout);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'f' || e.key === 'F' || e.code === 'Space')) {
        const el = document.activeElement as HTMLElement | null;
        if (e.key.toLowerCase() === 'f' && el && el.closest('[data-grid-search]')) return; // grid kendi aramasını alır
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  const items = useMemo<Item[]>(() => {
    const screens: Item[] = flat
      .filter((n) => isLeaf(n))
      .map((n) => ({
        id: n.screenId,
        group: 'screens',
        label: n.caption,
        hint: n.path.join(' › '),
        node: n,
        run: () => openScreen(n),
      }));
    const commands: Item[] = [
      { id: 'cmd:settings', group: 'commands', label: t('prefs.title'), run: () => setSettings(true) },
      {
        id: 'cmd:dark',
        group: 'commands',
        label: `${t('prefs.theme.label')}: ${t('prefs.theme.dark')}`,
        run: () => setPrefs({ theme: 'dark' }),
      },
      {
        id: 'cmd:light',
        group: 'commands',
        label: `${t('prefs.theme.label')}: ${t('prefs.theme.light')}`,
        run: () => setPrefs({ theme: 'light' }),
      },
      { id: 'cmd:logout', group: 'commands', label: t('auth.logout'), run: () => void logout('manual') },
    ];
    // Tasarım modu profil menüsünden çıkarıldı (kabuk ayarı değil, seyrek kullanılan bir
    // araç) ve komut olarak buraya taşındı; yalnızca sistem rolünde anlamlı.
    if (isSysRol) {
      commands.push({
        id: 'cmd:design',
        group: 'commands',
        label: `${t('shell.user.designMode')}: ${designMode ? t('common:Off') : t('common:On')}`,
        run: () => setDesignMode(!designMode),
      });
    }
    return [...screens, ...commands];
  }, [flat, t, openScreen, setSettings, setPrefs, logout, isSysRol, designMode, setDesignMode]);

  const results = useMemo(() => {
    if (q.trim().length < 2) return items.filter((i) => i.group === 'commands').slice(0, 8);
    return fuzzysort.go(q, items, { key: 'label', limit: 30, threshold: -10000 }).map((r) => r.obj);
  }, [q, items]);

  const run = (item: Item | undefined) => {
    if (!item) return;
    item.run();
    setOpen(false);
    setQ('');
  };

  return (
    <RadixDialog.Root open={open} onOpenChange={setOpen}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-[var(--overlay)]" />
        <RadixDialog.Content className="border-border bg-bg-elevated fixed top-[12vh] left-1/2 z-50 w-[min(92vw,40rem)] -translate-x-1/2 overflow-hidden rounded-xl border shadow-[var(--shadow)] focus:outline-none">
          <RadixDialog.Title className="sr-only">{t('shell.search')}</RadixDialog.Title>
          <div className="border-border flex items-center gap-2 border-b px-3">
            <Search className="text-fg-subtle h-4 w-4" />
            <input
              autoFocus
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setIdx(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') setIdx((i) => (i + 1) % Math.max(results.length, 1));
                else if (e.key === 'ArrowUp')
                  setIdx((i) => (i - 1 + Math.max(results.length, 1)) % Math.max(results.length, 1));
                else if (e.key === 'Enter') run(results[idx]);
              }}
              placeholder={t('shell.spotlight.placeholder')}
              className="placeholder:text-fg-subtle h-12 w-full bg-transparent text-base outline-none"
            />
          </div>
          <ul role="listbox" className="max-h-[50vh] overflow-y-auto p-1">
            {results.length === 0 ? (
              <li className="text-fg-muted px-3 py-6 text-center text-sm">{t('shell.spotlight.empty')}</li>
            ) : null}
            {results.map((r, i) => (
              <li
                key={r.id}
                role="option"
                aria-selected={i === idx}
                onMouseEnter={() => setIdx(i)}
                onClick={() => run(r)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm',
                  i === idx && 'bg-bg-hover',
                )}
              >
                {r.node ? <NavIcon node={r.node} /> : <span className="text-fg-subtle h-4 w-4">›</span>}
                <span className="min-w-0 flex-1 truncate">{r.label}</span>
                {r.hint ? <span className="text-fg-subtle truncate text-xs">{r.hint}</span> : null}
                <span className="text-fg-subtle text-[10px] uppercase">
                  {t(`shell.spotlight.${r.group}`)}
                </span>
              </li>
            ))}
          </ul>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
